import React, { useEffect, useState } from "react";
import { CreditCard, Loader2, Link2Off, CheckCircle2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { cardCls, Btn, Input } from "@/components/admin/ui";

const money = (minor, currency) => {
  try { return new Intl.NumberFormat("en-GB", { style: "currency", currency: (currency || "gbp").toUpperCase() }).format(minor / 100); }
  catch { return `${(minor / 100).toFixed(2)} ${currency}`; }
};

export default function StripeRevenueCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/stripe/revenue").then(({ data }) => setData(data)).catch(() => setData({ connected: false })).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (!key.trim()) return;
    setBusy(true);
    try { await api.post("/admin/stripe/connect", { secret_key: key.trim() }); toast.success("Stripe connected"); setKey(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Stripe?")) return;
    await api.post("/admin/stripe/disconnect"); toast.success("Stripe disconnected"); load();
  };

  return (
    <div data-testid="stripe-card" className={`${cardCls} p-6`}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <CreditCard size={20} className="text-bd-gold" />
          <h2 className="font-serif text-xl text-bd-ivory">Live revenue — Stripe</h2>
          {data?.connected && (
            <span className={`px-2 py-0.5 text-[0.6rem] tracking-wider uppercase border ${data.livemode ? "border-emerald-400/50 text-emerald-400" : "border-bd-gold/50 text-bd-gold"}`}>
              {data.livemode ? "Live" : "Test"}
            </span>
          )}
        </div>
        {data?.connected && (
          <div className="flex gap-2">
            <button onClick={load} className="p-2 text-bd-ivory/40 hover:text-bd-gold" title="Refresh"><RefreshCw size={15} /></button>
            <button data-testid="stripe-disconnect" onClick={disconnect} className="p-2 text-bd-ivory/40 hover:text-red-400" title="Disconnect"><Link2Off size={15} /></button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-8 text-center text-bd-ivory/40"><Loader2 className="animate-spin inline" size={20} /></div>
      ) : data?.connected ? (
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div>
              <p className="text-xs uppercase tracking-wider text-bd-ivory/40 mb-1">Gross captured</p>
              <p className="font-serif text-3xl text-bd-gold">
                {Object.keys(data.gross_captured_minor || {}).length
                  ? Object.entries(data.gross_captured_minor).map(([c, n]) => money(n, c)).join("  ")
                  : money(0, "gbp")}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-bd-ivory/40 mb-1">Succeeded charges</p>
              <p className="font-serif text-3xl text-bd-ivory">{data.charge_count}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-bd-ivory/40 mb-1">Available balance</p>
              <p className="font-serif text-3xl text-bd-ivory">
                {(data.available || []).length ? data.available.map((x) => money(x.amount_minor, x.currency)).join("  ") : money(0, "gbp")}
              </p>
            </div>
          </div>
          <p className="text-xs text-bd-ivory/30 flex items-center gap-2">
            <CheckCircle2 size={13} className="text-emerald-400" /> Real figures pulled directly from Stripe{data.livemode ? "" : " (test mode)"}. Gross captured excludes fees, refunds and disputes.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-bd-ivory/60">
            Connect your Stripe account to replace demo figures with real revenue. Paste a Stripe secret key
            (<code className="text-bd-gold">sk_…</code>) or a read-only restricted key (<code className="text-bd-gold">rk_…</code>). It is stored securely on the server and never shown again.
          </p>
          {data?.error && <p className="text-sm text-red-400">{data.error}</p>}
          <div className="flex flex-col sm:flex-row gap-2 max-w-xl">
            <Input data-testid="stripe-key-input" type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="sk_live_… or rk_live_…" />
            <Btn data-testid="stripe-connect" onClick={connect} disabled={busy}>{busy ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />} Connect</Btn>
          </div>
        </div>
      )}
    </div>
  );
}
